import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button from "components/common/button/Button"
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput"
import HTMLCode from "components/common/HTMLCode"
import If from "components/common/If"
import Translate from "components/common/Translate"
import { uuid } from "config/app-constants"
import { graph } from "config/axios-context"
import { getFormContext } from "config/FormContext"
import React from "react"
import { useContext, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { IdName } from "types/common-types"
import { DropdownInputPropsType } from "types/form-types"
import { AgricultureInvestmentIdNameDocument, AgricultureInvestmentIdNameQuery, AgricultureInvestmentIdNameQueryVariables, AgricultureMaintenanceLogTypeListDocument, AgricultureMaintenanceLogTypeListQuery, AgricultureMaintenanceLogTypeListQueryVariables, AgricultureWorkInvestmentFilterInput, AgricultureWorkInvestmentSortInput, MaintenanceLogFilterInput, MaintenanceLogSortInput } from "types/graphql"
import { removeAt } from "utilities/app-utilities"
import { appendFilters } from "utilities/datalist/datalist-filter-utilities"
import useAppContext from "utilities/hooks/useAppContext"
import { ClassificationType } from "../../fruit/classifications/inputs/FruitClassificationMultiselectInput"
import FruitGroupMultiselectInput from "../../fruit/classifications/inputs/FruitGroupMultiselectInput"

export type WorkAgricultureMaintenanceLogModelType = {
    id: number|string,
    investment?: NonNullable<AgricultureInvestmentIdNameQuery['agricultureWorkInvestments']>['items'][0],
    type: IdName,
    maintenanceLogFruitGroups: ClassificationType[]
}

type WorkAgricultureMaintenanceLogFormFieldsPropsType = {
    value?: WorkAgricultureMaintenanceLogModelType[],
    onChange?: (s: WorkAgricultureMaintenanceLogModelType[] | undefined) => void,
    name: string
    required?: boolean,
    min?: number
    max?: number,
    seasonId?: number
}

const WorkAgricultureMaintenanceLogFormFields = (props: WorkAgricultureMaintenanceLogFormFieldsPropsType) => {
	const [t] = useTranslation();
    const context = useAppContext();
    const hiddenInputRef = useRef<HTMLInputElement>(null);
    const [logError, setLogError] = useState<string>('');
	const formContext = getFormContext(useContext);

    useEffect(() => {
        checkValidity();
    }, [props.value]);

    // #region configs
    const logTypeDropdownConfig: DropdownInputPropsType<IdName, MaintenanceLogFilterInput, MaintenanceLogSortInput> = {
        getItemId: i => i.id,
        required: true,
        view: {
            inputView: i => i?.name
        },
        dataSource: {
            request: async (params) => {
                let variables: AgricultureMaintenanceLogTypeListQueryVariables = {};
                if(params.searchText) {
                    variables.where = {
                        name: {
                            contains: params.searchText
                        }
                    }
                }

                var request = await graph<AgricultureMaintenanceLogTypeListQuery, AgricultureMaintenanceLogTypeListQueryVariables>({
                    query: AgricultureMaintenanceLogTypeListDocument,
                    variables
                });

                if(request.errors) {
                    context.notify('error', 'request.error');
                    throw request.errors;
                }

                return request.data!.maintenanceLogTypes!.items.map(i => {
                    return {
                        id: i.id,
                        name: i.name
                    };
                });
            }
        },
        label: "fields.type.name",
        customErrors: [{
            message: t('form.errors.required'),
            name: 'required',
            validate(value: IdName | undefined | null) {
                return value?.id ? true : false;
            }
        }]
    }

    const investmentDropdownConfig: DropdownInputPropsType<NonNullable<AgricultureInvestmentIdNameQuery['agricultureWorkInvestments']>['items'][0], AgricultureWorkInvestmentFilterInput, AgricultureWorkInvestmentSortInput> = {
        getItemId: i => i.id,
        required: false,
        view: {
            inputView: i => i?.name
        },
        dataSource: {
            request: async (params) => {
                let variables: AgricultureInvestmentIdNameQueryVariables = {
                    take: 5
                };
                if(props.seasonId) {
                    variables.where = {
                        seasonId: {
                            eq: props.seasonId
                        }
                    }
                }
                if(params.searchText) {
                    variables.where = appendFilters<AgricultureWorkInvestmentFilterInput>(variables.where, {
                        name: {
                            contains: params.searchText
                        }
                    })
                }

                var response = await graph<AgricultureInvestmentIdNameQuery, AgricultureInvestmentIdNameQueryVariables>({
                    query: AgricultureInvestmentIdNameDocument,
                    variables
                });

                if(response.errors) {
                    context.notify('error', response.errors[0].message);
                    throw response.errors;
                }

                return response.data!.agricultureWorkInvestments!.items;
            }
        },
        label: "fields.investment.name",
        name: 'investment'
    }
    // #endregion configs

    // #region functions
    const checkValidity = () => {
        let error = '';
        if (props.required && !props.value?.length) {
            error = 'required';
        }
        if (props.min && props.value?.length && props.value.length < props.min) {
            error = 'min';
        }
        if (props.max && props.value?.length && props.value.length > props.max) {
            error = 'max';
        }
        hiddenInputRef.current?.setCustomValidity(error);
        setLogError(error);
    }
    
	const onChange = (model?: WorkAgricultureMaintenanceLogModelType[]) => {
		if (props.onChange) props.onChange(model);
		checkValidity();
	};

    const getObj = () => {
        return {
            id: uuid(),
            investment: undefined,
            type: undefined as any,
            maintenanceLogFruitGroups: []
        }
    }

    const add = () => {
		let value = props.value ? [...props.value] : [];
		value.push(getObj())
		onChange(value);
    }

    const set = (log: Partial<WorkAgricultureMaintenanceLogModelType>, index: number) => {
		let value = props.value ? [...props.value] : [];
		value[index] = {
			...value[index],
			...log
		};

		onChange(value);
	};

    const remove = (index: number) => {
        let value = props.value ? [...props.value] : [];
		value = removeAt(value, index);
		if (props.required && !value.length) {
			value.push(getObj());
		}

        onChange(value);
    }
    // #endregion functions

    return (
        <>
            <h2 className={`${logError && formContext.submitted ? 'text-danger' : ''}`}>Logs</h2>
            <If condition={logError && formContext.submitted}>
                <span className="text-danger text-sm">
                    <Translate i18nKey={`form.errors.array.` + logError}/>
                </span>
			</If>
            <hr/>
            <input title="for validation only" ref={hiddenInputRef} className="hidden" />
            {props.value?.map((log, index) => {
                return (
                    <React.Fragment key={`${props.name}-maintenance-log-${log.id}`}>
                        <div className="hover:bg-neutral-100 dark:hover:bg-neutral-900">
                            <DropdownInput
                                {...logTypeDropdownConfig}
                                value={log.type}
                                onChange={type => set({type}, index)}
                            />
                            <FruitGroupMultiselectInput
                                fruitId={1}
                                required
                                value={log.maintenanceLogFruitGroups}
                                onChange={maintenanceLogFruitGroups => set({maintenanceLogFruitGroups}, index)}
                            />
                            <DropdownInput
                                {...investmentDropdownConfig}
                                value={log.investment}
                                onChange={investment => set({investment}, index)}
                            />
							<div className="w-full text-right">
                                <Button title={t('button.remove')} theme="link" className="text-danger mt-4"
                                    onClick={() => remove(index)}>
                                    <FontAwesomeIcon icon={faTrash}/>
                                </Button>
                            </div>
                        </div>
                    </React.Fragment>
                )
            })}
			<If condition={!props.max || (props.value?.length || 0) >= props.max}>
				<Button title={t('button.add')} className="mt-1" onClick={add}>
					<FontAwesomeIcon icon={faPlus}/>
					<HTMLCode/>
					<Translate i18nKey="button.add"/>
				</Button>
			</If>
        </>
    );
};

export default WorkAgricultureMaintenanceLogFormFields;
